var React = require('react');
var { createElement: h } = React;

function isClassifierNode(node) {
  return !!(node && node.kind === 'workflow-node' && node.icon === 'classifier');
}

function isRouterNode(node) {
  return !!(node && node.kind === 'workflow-node' && node.icon === 'router');
}

function isClassifierLikeNode(node) {
  return isClassifierNode(node) || isRouterNode(node);
}

function isOnFailNode(node) {
  return !!(node && node.kind === 'workflow-node' && node.icon === 'on-fail');
}

function getClassifierLabels(node) {
  var paramValues = node && node.paramValues ? node.paramValues : {};
  return String(paramValues.labels || '')
    .split(',')
    .map(function(item) { return item.trim(); })
    .filter(Boolean)
    .slice(0, 20);
}

function getRouterLabels(node) {
  return getClassifierLabels(node);
}

function getHighestConfidenceClassifierAnchor(node, output) {
  if (!isClassifierNode(node)) return null;
  var labels = getClassifierLabels(node);
  if (!labels.length || !output || typeof output !== 'object') return null;

  var confidences = output.confidences && typeof output.confidences === 'object'
    ? output.confidences
    : null;
  var bestIndex = -1;
  var bestScore = Number.NEGATIVE_INFINITY;
  var sawFiniteConfidence = false;

  if (confidences) {
    labels.forEach(function(label, index) {
      var rawScore = Number(confidences[label]);
      if (!Number.isFinite(rawScore)) return;
      sawFiniteConfidence = true;
      if (rawScore > bestScore) {
        bestScore = rawScore;
        bestIndex = index;
      }
    });
  }

  if (sawFiniteConfidence && bestScore <= 0) return 'bottom';
  if (bestIndex >= 0) return 'classifier-' + bestIndex;

  var classification = String(output.classification || '').trim().toLowerCase();
  if (!classification) return null;

  bestIndex = labels.findIndex(function(label) {
    return String(label || '').trim().toLowerCase() === classification;
  });

  return bestIndex >= 0 ? ('classifier-' + bestIndex) : null;
}

function isClassifierAnchor(anchor) {
  return typeof anchor === 'string' && anchor.indexOf('classifier-') === 0;
}

function getClassifierInputAnchors() {
  return ['top', 'left'];
}

function getClassifierAnchorIndex(anchor) {
  if (!isClassifierAnchor(anchor)) return -1;
  var index = Number(String(anchor).split('-')[1]);
  return Number.isFinite(index) ? index : -1;
}

function getClassifierAnchorOffset(anchor) {
  var index = getClassifierAnchorIndex(anchor);
  // Match the current classifier row layout:
  // header/padding + divider + first row center, then row height + list gap.
  var baseOffset = 86;
  var rowStep = 40;
  return index < 0 ? baseOffset : baseOffset + (index * rowStep);
}

function getCompactClassifierAnchorOffset(rect, anchor, node) {
  return rect.cy;
}

function anchorPoint(rect, anchor, node, classifierCompactMode) {
  if (isClassifierLikeNode(node) && isClassifierAnchor(anchor)) {
    if (classifierCompactMode) {
      return { x: rect.right, y: rect.cy };
    }
    var idx = getClassifierAnchorIndex(anchor);
    var offsetY = (rect.classifierRowOffsets && idx >= 0 && idx < rect.classifierRowOffsets.length)
      ? rect.classifierRowOffsets[idx]
      : getClassifierAnchorOffset(anchor);
    return { x: rect.right, y: rect.top + offsetY };
  }
  if (isClassifierLikeNode(node) && classifierCompactMode && anchor === 'bottom') {
    return { x: rect.right, y: rect.cy };
  }
  if (anchor === 'top') return { x: rect.cx, y: rect.top };
  if (anchor === 'bottom') return { x: rect.cx, y: rect.bottom };
  if (anchor === 'left') return { x: rect.left, y: rect.cy };
  if (anchor === 'right') return { x: rect.right, y: rect.cy };
  return { x: rect.cx, y: rect.cy };
}

function isConditionNode(node) {
  return !!(node && node.kind === 'workflow-node' && node.icon === 'condition');
}

function isLoopNode(node) {
  return !!(node && node.kind === 'workflow-node' && node.icon === 'loop');
}

function isCustomNode(node) {
  return !!(node && node.kind === 'workflow-node' && node.category === 'custom');
}

function isDuplicableNode(node) {
  return isCustomNode(node) || isAgentNode(node) || isClassifierNode(node) || isRouterNode(node);
}

function isAgentNode(node) {
  return !!(node && node.kind === 'workflow-node' && node.icon === 'agent' && node.category !== 'custom');
}

function isToolAgentNode(node) {
  return !!(node && node.kind === 'workflow-node' && node.icon === 'tool-agent');
}

// A connection that leaves a Tool Agent's bottom "tools" anchor wires a
// callable tool, not normal downstream flow. The runtime reserves these
// targets as tools instead of running them in the wave.
function isToolConnection(connection, fromNode) {
  return isToolAgentNode(fromNode) && String((connection && connection.fromAnchor) || '') === 'bottom';
}

function isVariablesNode(node) {
  return !!(node && node.kind === 'workflow-node' && node.icon === 'variables');
}

var CONDITION_ANCHORS = ['top', 'right', 'bottom', 'left'];

function isConditionAnchor(anchor) {
  return CONDITION_ANCHORS.indexOf(anchor) >= 0;
}

function getConditionAnchorOrder(inputAnchor) {
  if (inputAnchor === 'top') return ['right', 'bottom', 'left'];
  if (inputAnchor === 'right') return ['bottom', 'left', 'top'];
  if (inputAnchor === 'bottom') return ['left', 'top', 'right'];
  if (inputAnchor === 'left') return ['top', 'right', 'bottom'];
  return ['top', 'right', 'bottom', 'left'];
}

function getConditionDefaultBranchAnchors(inputAnchor) {
  var order = getConditionAnchorOrder(inputAnchor);
  return {
    yes: order[0] || 'right',
    no: order[1] || 'left'
  };
}

// Returns { input, yes, no } for a condition node.
function getConditionAxes(nodeId, axesMap) {
  return (axesMap && axesMap[nodeId]) || { input: null, yes: 'right', no: 'left' };
}

function getConditionBranchStateFromAnchor(node, fallbackAnchor) {
  var paramValues = node && node.paramValues ? node.paramValues : {};
  var inputAnchor = isConditionAnchor(fallbackAnchor) ? fallbackAnchor : null;
  var available = getConditionAnchorOrder(inputAnchor);
  var savedYes = isConditionAnchor(paramValues.conditionYesAnchor) ? String(paramValues.conditionYesAnchor) : '';
  var savedNo = isConditionAnchor(paramValues.conditionNoAnchor) ? String(paramValues.conditionNoAnchor) : '';
  var layout = String(paramValues.conditionBranchLayout || '').trim().toLowerCase();
  var yes = '';
  var no = '';

  if (layout === 'horizontal') {
    yes = 'right';
    no = 'left';
  } else if (layout === 'vertical') {
    yes = 'top';
    no = 'bottom';
  } else if (String(paramValues.conditionBranchSwap || 'false') === 'true') {
    yes = 'right';
    no = 'left';
  } else {
    yes = available[0] || 'right';
    no = available[1] || 'left';
  }

  if (savedYes && available.indexOf(savedYes) >= 0) yes = savedYes;
  if (savedNo && available.indexOf(savedNo) >= 0) no = savedNo;

  if (!yes || yes === inputAnchor || !isConditionAnchor(yes)) {
    yes = available[0] || null;
  }

  if (!no || no === inputAnchor || !isConditionAnchor(no) || no === yes) {
    no = available.find(function(anchor) { return anchor !== yes; }) || null;
  }

  return { input: inputAnchor, yes: yes, no: no };
}

function buildDuplicateNodeTitle(nodes, baseTitle) {
  var original = String(baseTitle || 'Custom node').trim() || 'Custom node';
  var root = original.replace(/\s+\(Copy(?:\s+\d+)?\)$/i, '').trim() || original;
  var existing = {};
  (nodes || []).forEach(function(node) {
    existing[String((node && node.title) || '').trim().toLowerCase()] = true;
  });

  var firstCandidate = root + ' (Copy)';
  if (!existing[firstCandidate.toLowerCase()]) return firstCandidate;

  for (var i = 2; i < 200; i += 1) {
    var candidate = root + ' (Copy ' + i + ')';
    if (!existing[candidate.toLowerCase()]) return candidate;
  }
  return root + ' (Copy ' + Date.now().toString(36) + ')';
}

// "API Key" -> "apiKey", "Basic Auth-Token" -> "basicAuthToken".
function deriveParamKey(label) {
  var raw = String(label || '').trim();
  if (!raw) return 'param';
  var parts = raw.split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (parts.length === 0) return 'param';
  var head = parts[0].toLowerCase();
  var tail = parts.slice(1).map(function(w) {
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).join('');
  var key = head + tail;
  if (/^\d/.test(key)) key = 'p' + key;
  return key || 'param';
}

// Disambiguate against an existing hyperparameters array by suffixing _2, _3...
function uniqueParamKey(baseKey, existingParams) {
  var used = {};
  (existingParams || []).forEach(function(p) {
    if (p && p.key) used[String(p.key)] = true;
  });
  if (!used[baseKey]) return baseKey;
  for (var i = 2; i < 1000; i += 1) {
    var candidate = baseKey + '_' + i;
    if (!used[candidate]) return candidate;
  }
  return baseKey + '_' + Date.now().toString(36);
}

var LOCKED_DUPLICATE_FIELDS = [
  'kind',
  'nodeType',
  'category',
  'title',
  'subtitle',
  'icon',
  'logoUrl',
  'templateId',
  'code',
  'generationPrompt',
  'changeHistory',
  'inputDescription',
  'agentDescription',
  'inputExample',
  'outputDescription',
  'label',
  'color',
  'borderColor',
  'options',
  'stats',
  'hyperparameters',
  'conditionRule',
  'size'
];

function getLockedDuplicateFields() {
  if (Array.isArray(LOCKED_DUPLICATE_FIELDS) && LOCKED_DUPLICATE_FIELDS.length) {
    return LOCKED_DUPLICATE_FIELDS;
  }
  return [
    'kind',
    'nodeType',
    'category',
    'title',
    'subtitle',
    'icon',
    'logoUrl',
    'templateId',
    'code',
    'generationPrompt',
    'changeHistory',
    'inputDescription',
    'agentDescription',
    'inputExample',
    'outputDescription',
    'label',
    'color',
    'borderColor',
    'options',
    'stats',
    'hyperparameters',
    'conditionRule',
    'size'
  ];
}

function clonePlainData(value) {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value.map(function(item) { return clonePlainData(item); });
  }
  if (typeof value === 'object') {
    var out = {};
    Object.keys(value).forEach(function(key) {
      out[key] = clonePlainData(value[key]);
    });
    return out;
  }
  return value;
}

function getDuplicateSourceNodeId(node) {
  var raw = node && node.duplicateSourceNodeId !== undefined ? String(node.duplicateSourceNodeId) : '';
  var trimmed = raw.trim();
  return trimmed || null;
}

function isLockedDuplicateNode(node) {
  return !!(node && node.duplicateLocked === true && getDuplicateSourceNodeId(node));
}

function buildInheritedDuplicateNode(baseNode, sourceNode) {
  if (!baseNode || !sourceNode) return baseNode;
  var merged = Object.assign({}, baseNode);
  var fields = getLockedDuplicateFields();
  for (var i = 0; i < fields.length; i += 1) {
    var field = fields[i];
    if (Object.prototype.hasOwnProperty.call(sourceNode, field)) {
      merged[field] = clonePlainData(sourceNode[field]);
    }
  }
  merged.id = baseNode.id;
  merged.position = baseNode.position;
  merged.duplicateSourceNodeId = baseNode.duplicateSourceNodeId;
  merged.duplicateLocked = baseNode.duplicateLocked;
  return merged;
}

function resolveEffectiveNodeById(nodeId, rawNodeById, cache, visiting) {
  if (!nodeId || !rawNodeById[nodeId]) return null;
  if (cache[nodeId]) return cache[nodeId];
  if (visiting[nodeId]) return rawNodeById[nodeId];

  var node = rawNodeById[nodeId];
  var sourceId = getDuplicateSourceNodeId(node);
  if (!(node && node.duplicateLocked === true && sourceId && sourceId !== nodeId)) {
    cache[nodeId] = node;
    return node;
  }

  visiting[nodeId] = true;
  var sourceNode = resolveEffectiveNodeById(sourceId, rawNodeById, cache, visiting);
  delete visiting[nodeId];
  if (!sourceNode) {
    cache[nodeId] = node;
    return node;
  }

  var merged = buildInheritedDuplicateNode(node, sourceNode);
  cache[nodeId] = merged;
  return merged;
}

function buildEffectiveNodes(nodes) {
  var rawNodes = Array.isArray(nodes) ? nodes : [];
  var rawNodeById = {};
  rawNodes.forEach(function(node) {
    if (!node || !node.id) return;
    rawNodeById[String(node.id)] = node;
  });

  var cache = {};
  return rawNodes.map(function(node) {
    if (!node || !node.id) return node;
    return resolveEffectiveNodeById(String(node.id), rawNodeById, cache, {}) || node;
  });
}

function LockIcon() {
  return h(
    'svg',
    { width: 11, height: 11, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true' },
    h('rect', { x: 4, y: 11, width: 16, height: 10, rx: 2 }),
    h('path', { d: 'M8 11V8a4 4 0 118 0v3' })
  );
}

function canAcceptIncomingConnection(node) {
  return !isVariablesNode(node);
}

function getNodeAnchors(node) {
  if (node && node.kind === 'workflow-node' && node.icon === 'start') return ['right'];
  if (isVariablesNode(node)) return [];
  if (isConditionNode(node)) return ['top', 'right', 'bottom', 'left'];
  if (isLoopNode(node)) return ['top', 'left', 'right'];
  // Tool Agent: left = input, right = final result output, bottom = tools.
  if (isToolAgentNode(node)) return ['left', 'right', 'bottom'];
  if (isClassifierLikeNode(node)) {
    var classifierLabels = getClassifierLabels(node);
    return classifierLabels.length
      ? getClassifierInputAnchors().concat(classifierLabels.map(function(_, index) { return 'classifier-' + index; }), ['bottom'])
      : getClassifierInputAnchors().concat(['bottom']);
  }
  return ['top', 'right', 'bottom', 'left'];
}

function getIncomingAnchors(node) {
  if (isConditionNode(node)) return ['top', 'right', 'bottom', 'left'];
  if (isClassifierLikeNode(node)) return getClassifierInputAnchors();
  // Tool Agent input arrives on the left only; right/bottom are outputs.
  if (isToolAgentNode(node)) return ['left'];
  return getNodeAnchors(node);
}

function canStartConnectionFromAnchor(node, anchor, axesMap, outgoingConnections) {
  if (isConditionNode(node)) {
    var state = getConditionAxes(node.id, axesMap);
    if (anchor === state.input) return false;
    // Condition nodes allow max 2 outgoing connections (yes + no).
    // Block new connections from anchors that don't already have one.
    if (Array.isArray(outgoingConnections) && outgoingConnections.length >= 2) {
      var anchorHasConnection = outgoingConnections.some(function(conn) {
        return String(conn.fromAnchor || '').trim().toLowerCase() === anchor;
      });
      return anchorHasConnection;
    }
    return true;
  }
  if (isClassifierLikeNode(node)) {
    return anchor !== 'top' && anchor !== 'left';
  }
  // Tool Agent: only right (result) and bottom (tools) start connections.
  if (isToolAgentNode(node)) {
    return anchor === 'right' || anchor === 'bottom';
  }
  return true;
}

function getAnchorTitle(node, anchor, axesMap) {
  if (isConditionNode(node)) {
    var state = getConditionAxes(node.id, axesMap);
    if (anchor === state.input) return 'Condition input connector';
    if (anchor === state.yes) return 'Condition yes connector';
    return 'Condition no connector';
  }
  if (isLoopNode(node) && anchor === 'left') return 'Each item';
  if (isLoopNode(node) && anchor === 'right') return 'Done';
  if (isClassifierLikeNode(node) && isClassifierAnchor(anchor)) {
    var classifierLabels = getClassifierLabels(node);
    var classifierIndex = getClassifierAnchorIndex(anchor);
    var connLabel = isRouterNode(node) ? 'Route connector' : 'Class connector';
    return classifierLabels[classifierIndex] ? ('Connector for ' + classifierLabels[classifierIndex]) : connLabel;
  }
  if (isClassifierLikeNode(node) && (anchor === 'top' || anchor === 'left')) return (isRouterNode(node) ? 'Router' : 'Classifier') + ' input connector';
  if (isClassifierLikeNode(node) && anchor === 'bottom') return 'Fallback connector';
  if (isToolAgentNode(node) && anchor === 'left') return 'Tool Calling input connector';
  if (isToolAgentNode(node) && anchor === 'right') return 'Tool Calling result connector';
  if (isToolAgentNode(node) && anchor === 'bottom') return 'Connect a node to use as a tool';
  return 'Create connector';
}

function getAnchorClass(node, anchor, axesMap) {
  if (isConditionNode(node)) {
    var axes = getConditionAxes(node.id, axesMap);
    if (anchor === axes.input) return ' connector-handle-input-only';
    if (anchor === axes.yes) return ' connector-handle-yes';
    return ' connector-handle-no';
  }
  if (isLoopNode(node) && anchor === 'left') return ' connector-handle-yes';
  if (isLoopNode(node) && anchor === 'right') return ' connector-handle-no';
  if (isClassifierLikeNode(node) && (anchor === 'top' || anchor === 'left')) return ' connector-handle-input-only';
  if (isClassifierLikeNode(node) && isClassifierAnchor(anchor)) return ' connector-handle-right connector-handle-classifier';
  return '';
}

function getAnchorLabel(node, anchor, axesMap) {
  // Condition nodes intentionally render no per-anchor "yes/no" dot labels.
  // The branch label now lives on the mid-line YES/NO pill (one per
  // connection), which is editable per connector — putting another label on
  // the dot was redundant and confusing once branches became per-connection.
  if (isConditionNode(node)) return null;
  if (isLoopNode(node) && anchor === 'left') return 'each';
  if (isLoopNode(node) && anchor === 'right') return 'done';
  return null;
}

function getConnectionClassName(connection, fromNode, fromAnchor, axesMap, toNode) {
  var base = connection.noWait ? 'conn-line conn-line-light' : (connection.className || 'conn-line');
  if (connection.noData) base += ' conn-line-cut';
  // Any edge INTO an On Fail node is a fail edge by definition — it only
  // carries flow when the source node errors, so it renders in failure red.
  if (isOnFailNode(toNode)) return base + ' conn-line-fail';
  if (isConditionNode(fromNode)) {
    // Per-connection branch label takes precedence over the per-node anchor
    // hint so siblings sharing an anchor can render as different colors.
    var explicit = connection && connection.branch ? String(connection.branch).trim().toLowerCase() : '';
    if (explicit === 'yes') return base + ' conn-line-yes';
    if (explicit === 'no') return base + ' conn-line-no';
    var axes = getConditionAxes(fromNode.id, axesMap);
    if (fromAnchor === axes.yes) return base + ' conn-line-yes';
    return base + ' conn-line-no';
  }
  if (isLoopNode(fromNode) && fromAnchor === 'left') return base + ' conn-line-yes';
  if (isLoopNode(fromNode) && fromAnchor === 'right') return base + ' conn-line-no';
  // Tool wires (bottom anchor) render dashed/dimmed to read as "tool", not flow.
  if (isToolAgentNode(fromNode) && fromAnchor === 'bottom') return base + ' conn-line-tool';
  return base;
}

function getConnectionPreviewClassName(sourceNode, fromAnchor, axesMap) {
  if (isConditionNode(sourceNode)) {
    var axes = getConditionAxes(sourceNode.id, axesMap);
    if (fromAnchor === axes.yes) return 'conn-line conn-line-preview conn-line-yes';
    return 'conn-line conn-line-preview conn-line-no';
  }
  if (isLoopNode(sourceNode) && fromAnchor === 'left') return 'conn-line conn-line-preview conn-line-yes';
  if (isLoopNode(sourceNode) && fromAnchor === 'right') return 'conn-line conn-line-preview conn-line-no';
  return 'conn-line conn-line-preview';
}

function getFutureCandidateAnchors(node) {
  if (!node) return [];
  if (node.kind === 'workflow-node' && node.icon === 'start') return ['right'];
  if (isConditionNode(node)) return [];
  if (isLoopNode(node)) return ['left', 'right'];
  if (isVariablesNode(node)) return [];
  return ['right'];
}

function getStartNodeOutput(node) {
  if (!node || node.icon !== 'start' || !Array.isArray(node.hyperparameters) || !node.hyperparameters.length) return undefined;
  var output = {};
  node.hyperparameters.forEach(function(param) {
    if (!param || !param.key) return;
    output[param.key] = (node.paramValues && node.paramValues[param.key] !== undefined)
      ? node.paramValues[param.key]
      : (param.default !== undefined ? param.default : '');
  });
  return Object.keys(output).length ? output : undefined;
}

function isTerminalNode(nodeId, connections) {
  return !(connections || []).some(function(connection) {
    return connection && connection.from === nodeId;
  });
}

function parseVariableContextValue(dataType, rawValue) {
  var type = String(dataType || '').toLowerCase();
  if (type === 'int' || type === 'integer' || type === 'number') {
    var n = Number(rawValue);
    return Number.isFinite(n) ? n : 0;
  }
  if (type === 'bool' || type === 'boolean') {
    var v = String(rawValue || '').trim().toLowerCase();
    if (v === 'true' || v === '1' || v === 'yes') return true;
    if (v === 'false' || v === '0' || v === 'no') return false;
    return Boolean(rawValue);
  }
  return rawValue !== undefined && rawValue !== null ? String(rawValue) : '';
}

function formatDurationMs(ms) {
  var safeMs = Number(ms);
  if (!Number.isFinite(safeMs) || safeMs < 0) safeMs = 0;
  return (safeMs / 1000).toFixed(1) + 's';
}

function getValueAtPropPath(source, propPath) {
  if (!propPath) return source;
  if (source === null || source === undefined || typeof source !== 'object') return undefined;
  var segments = String(propPath).split('.').filter(Boolean);
  var current = source;
  for (var i = 0; i < segments.length; i += 1) {
    if (current === null || current === undefined || typeof current !== 'object') return undefined;
    current = current[segments[i]];
  }
  return current;
}

function formatOutputHoverText(sourceOutput, propPath) {
  var value = getValueAtPropPath(sourceOutput, propPath);
  if (value === undefined) return 'No output yet';
  if (value === null) return 'Output: null';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      var d = new Date(value);
      if (!isNaN(d.getTime())) {
        var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        var hr = d.getHours(); var ampm = hr >= 12 ? 'PM' : 'AM'; hr = hr % 12 || 12;
        return 'Output: ' + months[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear() + ' ' + hr + ':' + String(d.getMinutes()).padStart(2,'0') + ' ' + ampm;
      }
    }
    return value.length > 180 ? ('Output: ' + value.slice(0, 180) + '...') : ('Output: ' + value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') return 'Output: ' + String(value);
  try {
    var json = JSON.stringify(value);
    return json.length > 180 ? ('Output: ' + json.slice(0, 180) + '...') : ('Output: ' + json);
  } catch (e) {
    return 'Output available';
  }
}

function getConnectionActionGroupStyle(connection) {
  return {
    left: Math.round((connection.from.x + connection.to.x) / 2) + 'px',
    top: Math.round((connection.from.y + connection.to.y) / 2 - 10) + 'px'
  };
}

function ExecutionBadge(props) {
  var status = props.status;
  var label = props.label;
  if (!status || !label) return null;

  var icon = status === 'running'
    ? h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
        h('circle', { cx: 12, cy: 12, r: 9 }),
        h('polyline', { points: '12 7 12 12 16 14' })
      )
    : h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
        h('circle', { cx: 12, cy: 12, r: 9 }),
        h('polyline', { points: '8 12 11 15 16 9' })
      );

  return h('div', { className: 'node-exec-badge node-exec-badge-' + status },
    h('span', { className: 'node-exec-badge-icon' }, icon),
    h('span', { className: 'node-exec-badge-label' }, label)
  );
}

// Tokens that, when seen as the VALUE of an error/status field on a node's
// output object, signal "this run was unsuccessful". Anchored at field/value
// level (not free-text grep) so we don't flag prose that merely mentions the
// word "error" — e.g. a summary node with output { text: "no errors found" }.
var ERROR_FIELD_KEYS = ['error', 'errors', 'iserror', 'has_error', 'haserror', 'failed', 'isfailed'];
var STATUS_FIELD_KEYS = ['status', 'state', 'result', 'outcome'];
var ERROR_STATUS_VALUES = ['error', 'failed', 'failure', 'unsuccessful', 'fail', 'ko', 'rejected'];

function isTruthyErrorFlag(value) {
  if (value === true) return true;
  if (typeof value === 'string') {
    var s = value.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
  }
  return false;
}

function isErrorStatusValue(value) {
  if (typeof value !== 'string') return false;
  var s = value.trim().toLowerCase();
  return ERROR_STATUS_VALUES.indexOf(s) !== -1;
}

// Heuristic: does this node output look like an unsuccessful run?
// Detects three common shapes a node can use to signal failure without
// throwing — what the autopilot checker calls "silent errors":
//   1. { error: true, ... }          — boolean error flag
//   2. { error: "something wrong" }  — non-empty error message string
//   3. { status: "failed", ... }     — status/state field with a failure value
// Returns false for plain primitives, arrays, and objects without these keys.
// Intentionally conservative: we want a tight signal that pairs with the
// yellow warning chip on the canvas, not a fuzzy text search.
function looksLikeErrorOutput(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  var keys = Object.keys(value);
  for (var i = 0; i < keys.length; i += 1) {
    var k = keys[i];
    var lower = k.toLowerCase();
    var v = value[k];
    if (ERROR_FIELD_KEYS.indexOf(lower) !== -1) {
      if (isTruthyErrorFlag(v)) return true;
      if (typeof v === 'string' && v.trim().length > 0) return true;
    }
    if (STATUS_FIELD_KEYS.indexOf(lower) !== -1 && isErrorStatusValue(v)) return true;
  }
  return false;
}

// Yellow circle warning chip — sibling of ExecutionBadge in the badges row.
// Render only when looksLikeErrorOutput(output) is true AND the autopilot
// checker is enabled (gating handled by the caller). Tooltip explains why
// it appeared so the user can drill into the output.
function NodeWarningBadge(props) {
  var label = props.label || 'Possible error';
  var title = props.title || 'Output looks like an error or unsuccessful response';
  return h('div', { className: 'node-exec-badge node-exec-badge-warning', title: title },
    h('span', { className: 'node-exec-badge-icon' },
      h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
        h('circle', { cx: 12, cy: 12, r: 9 }),
        h('line', { x1: 12, y1: 8, x2: 12, y2: 13 }),
        h('circle', { cx: 12, cy: 16.5, r: 0.6, fill: 'currentColor', stroke: 'none' })
      )
    ),
    h('span', { className: 'node-exec-badge-label' }, label)
  );
}

// Compact token count for the node badge: 1240 -> "1.2K", 980 -> "980".
function formatTokenCountShort(n) {
  var v = Number(n) || 0;
  if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
  if (v >= 1000) return (v / 1000).toFixed(1) + 'K';
  return String(v);
}

// Token-usage chip — sibling of ExecutionBadge in the badges row. Shows how many
// input/output tokens an agent node spent on its runtime LLM calls (↓ in, ↑ out).
// Render only when the node reported a non-zero tally (agent nodes only).
function NodeTokenBadge(props) {
  var tokens = props.tokens;
  if (!tokens || ((tokens.in || 0) === 0 && (tokens.out || 0) === 0)) return null;
  var title = 'Tokens used by this agent: '
    + (tokens.in || 0).toLocaleString() + ' in, '
    + (tokens.out || 0).toLocaleString() + ' out';
  return h('div', { className: 'node-exec-badge node-exec-badge-tokens', title: title },
    h('span', { className: 'node-exec-badge-label' },
      '↓' + formatTokenCountShort(tokens.in) + ' ↑' + formatTokenCountShort(tokens.out)
    )
  );
}

function normalizeSourceAnchorForNode(node, anchor, ownRect, otherRect, axesMap) {
  if (!isConditionNode(node) && !isLoopNode(node)) return anchor;
  if (isConditionNode(node)) return anchor;
  if (anchor === 'top' || anchor === 'left' || anchor === 'right') return anchor;
  if (!ownRect || !otherRect) return 'right';
  return otherRect.cx < ownRect.cx ? 'left' : 'right';
}

function normalizeTargetAnchorForNode(node, anchor, ownRect, otherRect) {
  if (!isConditionNode(node) && !isLoopNode(node)) return anchor;
  if (isConditionNode(node)) {
    // All 4 sides are valid inputs; return stored anchor directly
    if (anchor === 'top' || anchor === 'right' || anchor === 'bottom' || anchor === 'left') return anchor;
    // Fallback: pick side based on relative position
    if (!ownRect || !otherRect) return 'left';
    var dx = Math.abs(otherRect.cx - ownRect.cx);
    var dy = Math.abs(otherRect.cy - ownRect.cy);
    if (dy > dx) return otherRect.cy < ownRect.cy ? 'top' : 'bottom';
    return otherRect.cx < ownRect.cx ? 'left' : 'right';
  }
  if (anchor === 'top' || anchor === 'left' || anchor === 'right') return anchor;
  if (!ownRect || !otherRect) return 'right';
  return otherRect.cx < ownRect.cx ? 'left' : 'right';
}

function nearestAnchorForPoint(rect, point, anchors) {
  var availableAnchors = Array.isArray(anchors) && anchors.length
    ? anchors
    : ['top', 'right', 'bottom', 'left'];
  var winner = availableAnchors[0];
  var bestDistance = Number.POSITIVE_INFINITY;

  for (var i = 0; i < availableAnchors.length; i += 1) {
    var anchor = availableAnchors[i];
    var anchorPos = anchorPoint(rect, anchor);
    var dx = anchorPos.x - point.x;
    var dy = anchorPos.y - point.y;
    var dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < bestDistance) {
      bestDistance = dist;
      winner = anchor;
    }
  }

  return winner;
}

// Pick an incoming anchor that isn't already taken by another connection from
// the same source. Used so multiple router routes wired to the same target
// land on different sides of the target instead of stacking on one anchor and
// hiding each other. Falls back to the nearest anchor when every side is in use.
function pickFreeIncomingAnchor(rect, point, anchors, existingConnections, sourceId, targetId) {
  var preferred = nearestAnchorForPoint(rect, point, anchors);
  if (!Array.isArray(existingConnections) || !existingConnections.length) return preferred;

  var taken = {};
  for (var i = 0; i < existingConnections.length; i += 1) {
    var conn = existingConnections[i];
    if (!conn) continue;
    if (String(conn.from) !== String(sourceId)) continue;
    if (String(conn.to) !== String(targetId)) continue;
    var anchor = String(conn.toAnchor || '').trim().toLowerCase();
    if (anchor) taken[anchor] = true;
  }

  if (!taken[preferred]) return preferred;

  var candidates = Array.isArray(anchors) && anchors.length ? anchors : ['top', 'right', 'bottom', 'left'];
  var freeCandidates = candidates.filter(function(a) { return !taken[a]; });
  if (!freeCandidates.length) return preferred;
  return nearestAnchorForPoint(rect, point, freeCandidates);
}

function isResizableNode(node) {
  // Only Classifier and Router still render as wide cards; everything else
  // renders as a fixed-size tile (~132px square) so width-resize would just
  // stretch a square into a misaligned rectangle. Disabling resize for tile
  // nodes also keeps the connector handles visually anchored to the edge.
  if (!node || node.kind !== 'workflow-node' || node.nodeType !== 'card') return false;
  if (node.icon === 'start') return false;
  return node.icon === 'classifier' || node.icon === 'router';
}

// Strip stale `size` from nodes that are no longer resizable. Pre-tile-redesign
// workflows had explicit width/height (typically 220x80) saved per node; that
// stale size makes connection anchors and selection outlines render at the old
// rectangle bounds instead of the visible 132px tile. Run this once on
// hydration so old workflows self-heal without a manual migration.
function stripStaleNodeSize(nodes) {
  if (!Array.isArray(nodes)) return nodes;
  var changed = false;
  var next = nodes.map(function(n) {
    if (!n || !n.size || isResizableNode(n)) return n;
    changed = true;
    return Object.assign({}, n, { size: null });
  });
  return changed ? next : nodes;
}

module.exports = {
  isClassifierNode, isRouterNode, isClassifierLikeNode, getClassifierLabels, getRouterLabels, getHighestConfidenceClassifierAnchor,
  isClassifierAnchor, getClassifierInputAnchors, getClassifierAnchorIndex,
  getClassifierAnchorOffset, getCompactClassifierAnchorOffset, anchorPoint,
  isConditionNode, isLoopNode, isCustomNode, isDuplicableNode, isAgentNode, isOnFailNode,
  isToolAgentNode, isToolConnection,
  isVariablesNode, CONDITION_ANCHORS, isConditionAnchor, getConditionAnchorOrder,
  getConditionDefaultBranchAnchors, getConditionAxes, getConditionBranchStateFromAnchor,
  buildDuplicateNodeTitle, LOCKED_DUPLICATE_FIELDS, getLockedDuplicateFields,
  clonePlainData, getDuplicateSourceNodeId, isLockedDuplicateNode,
  buildInheritedDuplicateNode, resolveEffectiveNodeById, buildEffectiveNodes,
  LockIcon, canAcceptIncomingConnection, getNodeAnchors, getIncomingAnchors,
  canStartConnectionFromAnchor, getAnchorTitle, getAnchorClass, getAnchorLabel,
  getConnectionClassName, getConnectionPreviewClassName, getFutureCandidateAnchors,
  getStartNodeOutput, isTerminalNode, parseVariableContextValue, formatDurationMs,
  getValueAtPropPath, formatOutputHoverText, getConnectionActionGroupStyle,
  ExecutionBadge, NodeWarningBadge, NodeTokenBadge, looksLikeErrorOutput,
  normalizeSourceAnchorForNode, normalizeTargetAnchorForNode,
  nearestAnchorForPoint, pickFreeIncomingAnchor, isResizableNode, stripStaleNodeSize,
  deriveParamKey, uniqueParamKey
};
